import { useEffect } from 'react';
import PlatForms from '@mono-farmart-web/common-utils/constant/platFormEnums';
import { logger } from '@mono-farmart-web/logger';
import { serializeError } from '@mono-farmart-web/common-utils/utils/serializeError';
import { reportApiError } from '@mono-farmart-web/api-utils/utils/apiCaller/reportApiError';
import { useLocalStorageStore } from '@mono-farmart-web/state-management';
import { extractUtmParams } from '@mono-farmart-web/common-utils/utils/handleUrlParams';
import {
	getSessionStorageItem,
	removeSessionStorageItem,
} from '@mono-farmart-web/common-utils/utils/common';
import { chronologMerchantUserId, sendChronologEvent } from '../chronolog';

/**
 * Native MoEngage (Capacitor plugin): user identity + push/in-app only.
 * Analytics events use the Web SDK by default; native `trackEvent` matches Retail-App-X only when
 * `trackEvent(..., PlatForms.get('application'))` → `bridge.emit('triggerEventOnApp', …)` (see bridgeFunctions `triggerEventOnApp`).
 */
export type CapacitorMoEngageHandlers = {
	addUniqueId: (mobileNumber: number | string) => void;
	updateUniqueUserId: (mobileNumber: number | string) => void;
	logout: () => void;
};

let capacitorMoEngageHandlers: CapacitorMoEngageHandlers | null = null;

export function registerCapacitorMoEngageHandlers(
	handlers: CapacitorMoEngageHandlers,
): void {
	capacitorMoEngageHandlers = handlers;
}

export function unregisterCapacitorMoEngageHandlers(): void {
	capacitorMoEngageHandlers = null;
}

// Module-level flag to ensure initialization happens only once
let isInitializing = false;
let isInitialized = false;

/**
 * Whether the host app opted into MoEngage at all — set by `useMoengage` /
 * `initializeMoengage`. Apps that never call either (e.g. `tracking-service`)
 * leave this false, which is how `trackEvent` tells "this app has no MoEngage"
 * apart from "MoEngage is configured but hasn't loaded yet".
 */
let moengageExpected = false;
let warnedMoengageAbsent = false;

/** Fired after the remote Web SDK has created the usable MoEngage instance. */
export const MOENGAGE_WEB_SDK_READY_EVENT = 'farmart:moengage-web-sdk-ready';

export const initializeMoengage = (
	appID: string,
	debugMode = 1,
	moeDataCenter = 'dc_3',
) => {
	moengageExpected = true;

	// Prevent multiple initializations
	//@ts-expect-error
	if (isInitialized || window.Moengage || isInitializing) {
		return;
	}

	isInitializing = true;
	console.log('initializing moengage', { appID });

	const scriptTag = document.createElement('script');
	scriptTag.type = 'text/javascript';
	scriptTag.text = `
    var moeDataCenter = "${moeDataCenter}"; // Replace "DC" with the actual Data center value from the above table
    var moeAppID = "${appID}"; // Replace "AppID" available in the settings page of MoEngage Dashboard.

	!function(e,n,i,t,a,r,o,d){if(!moeDataCenter||"{DC}"===moeDataCenter)return console.error("Data center has not been passed correctly. Please follow the SDK installation instruction carefully.");var s=e[a]=e[a]||[];if(s.invoked=0,s.initialised>0||s.invoked>0)return console.error("MoEngage Web SDK initialised multiple times. Please integrate the Web SDK only once!"),!1;e.moengage_object=a;var l={},g=function n(i){return function(){for(var n=arguments.length,t=Array(n),a=0;a<n;a++)t[a]=arguments[a];(e.moengage_q=e.moengage_q||[]).push({f:i,a:t})}},u=["track_event","add_user_attribute","add_first_name","add_last_name","add_email","add_mobile","add_user_name","add_gender","add_birthday","destroy_session","add_unique_user_id","update_unique_user_id","moe_events","call_web_push","track","location_type_attribute"],m={onsite:["getData","registerCallback"]};for(var c in u)l[u[c]]=g(u[c]);for(var v in m)for(var f in m[v])null==l[v]&&(l[v]={}),l[v][m[v][f]]=g(v+"."+m[v][f]);r=n.createElement(i),o=n.getElementsByTagName("head")[0],r.async=1,r.src=t,o.appendChild(r),e.moe=e.moe||function(){return(s.invoked=s.invoked+1,s.invoked>1)?(console.error("MoEngage Web SDK initialised multiple times. Please integrate the Web SDK only once!"),!1):(d=arguments.length<=0?void 0:arguments[0],l)},r.addEventListener("load",function(){if(d)return e[a]=e.moe(d),e[a].initialised=e[a].initialised+1||1,e.dispatchEvent(new Event("${MOENGAGE_WEB_SDK_READY_EVENT}")),!0}),r.addEventListener("error",function(){return console.error("Moengage Web SDK loading failed."),!1})}(window,document,"script","https://cdn.moengage.com/release/"+moeDataCenter+"/moe_webSdk.min.latest.js","Moengage");
    Moengage = moe({
      app_id: '${appID}',
      debug_logs: ${Number(debugMode)},
      enableSPA: true
    });
      `;

	// Mark as initialized when script loads successfully
	scriptTag.addEventListener('load', () => {
		isInitialized = true;
		isInitializing = false;
	});

	scriptTag.addEventListener('error', () => {
		isInitializing = false;
	});

	document.head.appendChild(scriptTag);
};

export const checkMoengage = () => {
	//@ts-expect-error
	const MoengageInstance = window.Moengage;
	return MoengageInstance || false;
};

export const useMoengage = (
	appID: string,
	debugMode = 1,
	moeDataCenter = 'dc_3',
	skipWebSdk = false,
) => {
	// Use useEffect to ensure initialization happens only once per component mount
	// This prevents multiple initializations on route changes or re-renders
	useEffect(() => {
		// Marked inside the effect, not during render: assigning module state while
		// rendering is a side effect React may repeat or discard. Set ahead of the
		// `skipWebSdk` return because the app opted into MoEngage either way.
		//
		// Consequence: React flushes child effects before the parent's, so events a
		// child fires on the very first commit still see this as false and are
		// dropped quietly rather than reported. That is the right trade — those
		// events are lost regardless, since the SDK script loads asynchronously.
		moengageExpected = true;

		if (skipWebSdk) {
			return;
		}
		initializeMoengage(appID, debugMode, moeDataCenter);
	}, [appID, debugMode, moeDataCenter, skipWebSdk]);

	const moengage = checkMoengage();

	return {
		moengage,
	};
};

export const trackEvent = (
	eventName: string,
	attrObj: Record<string, string | number | boolean | any>,
	platform?: string | null,
) => {
	const resolvedName =
		typeof eventName === 'string'
			? eventName.trim()
			: eventName != null
				? String(eventName).trim()
				: '';
	if (!resolvedName) {
		return;
	}

	const { userLanguage, userProfile } = useLocalStorageStore.getState();

	// Extract UTM parameters from current URL if available
	let utmParams: Record<string, string> = {};
	try {
		if (typeof window !== 'undefined' && window.location?.search) {
			utmParams = extractUtmParams(window.location.search);
		}
	} catch (_error) {
		// Silently fail if URL parsing fails
	}

	const updatedAttrObj = {
		...attrObj,
		Language: userLanguage?.langCode,
		// Include UTM parameters if available in current URL
		...(Object.keys(utmParams).length > 0 ? utmParams : {}),
	};
	void sendChronologEvent(resolvedName, updatedAttrObj, {
		userId: chronologMerchantUserId(userProfile?.merchant_detail_id),
		platform,
	});

	// Retail-App-X: WebView called `triggerEventOnApp` → native `trackMOEvent` only (no web SDK for that path).
	// Imported lazily so the native bridge (and with it `@capacitor/app`) stays out
	// of web-only bundles. `tracking-service` reaches this module through
	// `trip-tracking`, and it runs standalone and as an iframe inside the Android
	// WebView, where the native plugin bridge is not wired up.
	if (platform === PlatForms.get('application')) {
		void import('@mono-farmart-web/capacitor-bridge')
			.then(({ createCapacitorBridge }) =>
				createCapacitorBridge().emit('triggerEventOnApp', {
					eventName: resolvedName,
					attrObj: updatedAttrObj,
				}),
			)
			.catch(err => {
				reportApiError(
					new Error(
						`[analytics] native event emit failed: ${serializeError(err)}`,
					),
					{ eventName: resolvedName },
				);
			});
		return;
	}

	const moengage = checkMoengage();
	if (moengage) {
		moengage.track_event(resolvedName, updatedAttrObj);
		return;
	}

	// Apps that never opt into MoEngage (e.g. `tracking-service`) still render
	// shared components that track events. Dropping the event is the expected
	// outcome there, not a fault worth reporting to Sentry — only an app that
	// asked for MoEngage and hasn't got it yet is actually broken.
	if (!moengageExpected) {
		if (!warnedMoengageAbsent) {
			warnedMoengageAbsent = true;
			logger.info('moengage not configured for this app; dropping events', {
				eventName: resolvedName,
			});
		}
		return;
	}

	logger.err('event track sent but moengage not yet initialised.', {
		eventName: resolvedName,
	});
};

export const addUniqueId = (mobileNumber: number | string) => {
	if (capacitorMoEngageHandlers) {
		capacitorMoEngageHandlers.addUniqueId(mobileNumber);
	}
	const moengage = checkMoengage();
	if (moengage) {
		moengage.add_unique_user_id(mobileNumber)?.then(() => {
			moengage.add_mobile(mobileNumber);
		});
	}
};

export const updateUniqueUserId = (mobileNumber: number | string) => {
	if (capacitorMoEngageHandlers) {
		capacitorMoEngageHandlers.updateUniqueUserId(mobileNumber);
	}
	const moengage = checkMoengage();
	if (moengage) {
		moengage.update_unique_user_id(mobileNumber)?.then(() => {
			moengage.add_mobile(mobileNumber);
		});
	}
};

/**
 * Logout from MoEngage by calling destroy_session.
 * This is the recommended way to clear user session data instead of manually
 * manipulating MOE_DATA in localStorage.
 */
export const logoutMoengage = () => {
	if (capacitorMoEngageHandlers) {
		capacitorMoEngageHandlers.logout();
	}
	const moengage = checkMoengage();
	if (moengage && typeof moengage.destroy_session === 'function') {
		try {
			moengage.destroy_session();
			logger.info('MoEngage logout successful');
		} catch (error) {
			reportApiError(error, { context: 'moengage.logout' });
		}
	} else if (!capacitorMoEngageHandlers) {
		console.warn('MoEngage not initialized, skipping logout');
	}
};

/**
 * Simple pathname formatter for screen_name tracking
 * - Removes leading `/` and query params
 * - For `/trades/mySauda`, returns `mySauda`
 * - Checks for bottom navbar navigation
 * - Handles `/login` by getting previous pathname
 */
export const formatPathnameForTracking = (
	pathname: string | null | undefined,
	locationState?: any,
): string => {
	if (!pathname) return '';

	// Check bottom navbar first
	const fromBottomNavbar = getSessionStorageItem('fromBottomNavbar');
	if (fromBottomNavbar === 'my_sauda') {
		removeSessionStorageItem('fromBottomNavbar');
		return 'bottomNavbarMySauda';
	}
	if (fromBottomNavbar === 'community') {
		removeSessionStorageItem('fromBottomNavbar');
		return 'bottomNavbarCommunity';
	}

	// Remove query params
	const path = pathname.split('?')[0];

	// If login, get previous pathname
	if (path === '/login' || path === 'login') {
		const prev =
			locationState?.redirectTo || getSessionStorageItem('loginPreviousPathname');
		if (prev && prev !== '/login' && prev !== 'login') {
			return formatPathnameForTracking(prev);
		}
		return '';
	}

	// Remove leading `/`
	const cleaned = path.replace(/^\/+/, '');

	// For trades/mySauda, return just mySauda
	if (cleaned === 'trades/mySauda' || cleaned.startsWith('trades/mySauda')) {
		return 'mySauda';
	}

	return cleaned;
};

// Other MoEngage-related functions can be added here
